/* POSHRADS — predictive-search.js
   Shopify-native predictive search for the header search shell.
   - Debounced requests to routes.predictive_search_url with section_id=predictive-search (Section Rendering);
     the returned HTML (sections/predictive-search.liquid) is injected into the results panel.
   - Loading / result-count / no-results announced in a polite live region.
   - Keyboard: ArrowDown from the input enters the results; ArrowUp/ArrowDown move between result links;
     ArrowUp on the first link returns to the input. Escape / focus return are handled by header.js
     (closing the search shell returns focus to the search button).
   - Fallback: on any error (or without JS) the form still submits to /search normally.
   One delegated set of listeners, registered once. No dependencies. */
(() => {
  'use strict';

  if (window.poshPredictiveReady) return;
  window.poshPredictiveReady = true;

  const DELAY = 250;
  const MIN_CHARS = 2;
  let timer = null;
  let controller = null;

  const panelFor = (input) => document.getElementById(input.getAttribute('aria-controls'));
  const setStatus = (input, text) => {
    const status = document.getElementById(input.dataset.statusId);
    if (status) status.textContent = text;
  };

  const clear = (input) => {
    const panel = panelFor(input);
    if (!panel) return;
    panel.innerHTML = '';
    panel.hidden = true;
  };

  const search = async (input) => {
    const panel = panelFor(input);
    const terms = input.value.trim();
    if (!panel) return;
    if (terms.length < MIN_CHARS) {
      if (controller) controller.abort();
      controller = null;
      clear(input);
      setStatus(input, '');
      panel.removeAttribute('aria-busy');
      return;
    }

    // Only the current request may touch the UI: a superseded one never clears or replaces newer state.
    if (controller) controller.abort();
    const current = (controller = new AbortController());
    const isCurrent = () => controller === current;
    panel.setAttribute('aria-busy', 'true');
    setStatus(input, input.dataset.loadingText || '');

    const params = new URLSearchParams({
      q: terms,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,article,page',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });

    try {
      const response = await fetch(`${input.dataset.predictiveUrl}?${params}`, { signal: current.signal });
      if (!response.ok) throw new Error(`Predictive search ${response.status}`);
      const text = await response.text();
      if (!isCurrent()) return;
      const html = new DOMParser().parseFromString(text, 'text/html');
      const results = html.querySelector('[data-predictive-results-root]');
      if (!results) {
        clear(input);
        setStatus(input, '');
        return;
      }
      panel.replaceChildren(results);
      panel.hidden = false;
      const status = results.querySelector('[data-predictive-status]');
      setStatus(input, status ? status.textContent.trim() : '');
    } catch (error) {
      if (error.name === 'AbortError' || !isCurrent()) return;
      clear(input);
      setStatus(input, '');
    } finally {
      if (isCurrent()) panel.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-predictive-input]');
    if (!input) return;
    clearTimeout(timer);
    timer = setTimeout(() => search(input), DELAY);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const input = event.target.closest('[data-predictive-input]');
    const panel = input ? panelFor(input) : event.target.closest('[data-predictive-panel]');
    if (!panel || panel.hidden) return;
    const links = [...panel.querySelectorAll('a[href]')];
    if (!links.length) return;

    if (input) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[0].focus();
      }
      return;
    }

    event.preventDefault();
    const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      links[Math.min(index + 1, links.length - 1)].focus();
    } else if (index <= 0) {
      const owner = document.getElementById(panel.dataset.inputId);
      if (owner) owner.focus();
    } else {
      links[index - 1].focus();
    }
  });
})();
